/**
 * Phantom key. Never present at runtime — it exists only so the token carries
 * its service type through inference. Declared as a unique symbol so it cannot
 * collide with a real property and cannot be written by accident.
 */
declare const SERVICE_TYPE: unique symbol;

/**
 * A typed key into the service container. Replaces the legacy `createServices`
 * god-function: each feature contributes its own repositories, and consumers ask
 * for them by token instead of reaching into a hand-maintained object literal.
 *
 * The phantom is covariant in `T`, so `ServiceToken<CompaniesRepository>` is
 * assignable to `ServiceToken<unknown>` and heterogeneous collections of
 * bindings typecheck.
 */
export interface ServiceToken<out T> {
  readonly key: string;
  readonly [SERVICE_TYPE]?: T;
}

export function createServiceToken<T>(key: string): ServiceToken<T> {
  return { key };
}

export interface ServiceBinding<out T = unknown> {
  readonly token: ServiceToken<T>;
  readonly value: T;
}

/** Pairs a token with a value while keeping the two types tied together. */
export function bindService<T>(
  token: ServiceToken<T>,
  value: T,
): ServiceBinding<T> {
  return { token, value };
}

export interface ServiceContainer {
  resolve<T>(token: ServiceToken<T>): T;
  tryResolve<T>(token: ServiceToken<T>): T | undefined;
  has(token: ServiceToken<unknown>): boolean;
}
